import { formatPrice } from "../../../../../../common/helpers/formatPrice";
import { extractCurrencyInfo } from "../../../../../../common/helpers/currency";
import type { RawCartItem } from "../../../../../../types";
import type {
  TransformedCartItem,
  TransformedCartItemVariation,
  TransformedQuantityLimits,
  TransformedCartItemTotals,
} from "../../../types/cartApiTypes";

interface LanguageInfo {
  currentLanguage: string;
  shouldTranslate: boolean;
}

export const transformCartItem = (
  item: RawCartItem,
  languageInfo: LanguageInfo,
): TransformedCartItem => {
  const currencyInfo = extractCurrencyInfo(item.prices);

  // --- 1. Handle Translations ---
  let finalName = item.name;
  if (languageInfo.shouldTranslate && item.extensions.product_translations) {
    const translations = item.extensions.product_translations;
    const currentLang = languageInfo.currentLanguage;
    if (
      translations &&
      !Array.isArray(translations) &&
      translations[currentLang]?.name
    ) {
      finalName = translations[currentLang].name;
    }
  }

  // --- 2. Handle Variations & Attributes ---
  let finalVariation: TransformedCartItemVariation[] = item.variation.map(
    (v) => ({ ...v }),
  );
  if (
    item.extensions.enriched_attributes &&
    item.extensions.enriched_attributes.attributes.length > 0
  ) {
    finalVariation = item.extensions.enriched_attributes.attributes.map(
      (attr) => {
        const term = attr.terms[0];
        return {
          attribute:
            languageInfo.shouldTranslate && attr.translated_name
              ? attr.translated_name
              : attr.name,
          value:
            languageInfo.shouldTranslate && term.translated_name
              ? term.translated_name
              : term.name,
        };
      },
    );
  }

  // --- 3. Handle Quantity Limits ---
  const quantityLimits: TransformedQuantityLimits = {
    minimum: item.quantity_limits.minimum,
    maximum: item.quantity_limits.maximum,
    multipleOf: item.quantity_limits.multiple_of,
    editable: item.quantity_limits.editable,
  };

  // --- 4. Presentation Math: Line Totals ---
  // Store API returns strings in minor units. We parse them to integers for robust addition.
  const rawTotalInt = parseInt(item.totals.line_total || "0", 10);
  const rawTaxInt = parseInt(item.totals.line_total_tax || "0", 10);

  // Calculate Gross Total (Net + Tax) to ensure we match the Cart Total exactly
  const rawGrossInt = rawTotalInt + rawTaxInt;

  const itemTotals: TransformedCartItemTotals = {
    lineSubtotal: formatPrice(item.totals.line_subtotal, currencyInfo),
    lineSubtotalTax: formatPrice(item.totals.line_subtotal_tax, currencyInfo),
    lineTotal: formatPrice(item.totals.line_total, currencyInfo),
    lineTotalTax: formatPrice(item.totals.line_total_tax, currencyInfo),
    rawLineSubtotal: item.totals.line_subtotal,
    rawLineSubtotalTax: item.totals.line_subtotal_tax,
    rawLineTotal: item.totals.line_total,
    rawLineTotalTax: item.totals.line_total_tax,
    lineGrossTotal: formatPrice(String(rawGrossInt), currencyInfo),
    rawLineGrossTotal: rawGrossInt,
  };

  // --- 5. Catalog Price & ID Mapping (Enriched) ---
  const extras = item.extensions.cart_item_extras;
  const parentId = extras?.parent_id ?? 0;

  // Convert catalog price from DB format to minor units for formatPrice
  const rawCatalogVal = extras?.catalog_price || "0";
  const decimals = currencyInfo.currencyMinorUnit || 2;
  const minorUnitCatalogPrice = String(
    Math.round(parseFloat(rawCatalogVal) * Math.pow(10, decimals)),
  );
  const catalogPrice = formatPrice(minorUnitCatalogPrice, currencyInfo);

  // --- 6. Promotion Mapping ---
  const promotion = extras?.promo_eligible
    ? {
        isEligible: true,
        buyQty: extras.promo_buy,
        payQty: extras.promo_pay,
      }
    : undefined;

  const rawFirstOrder = item.extensions.first_order_discount;
  const firstOrderDiscount = rawFirstOrder?.is_eligible
    ? {
        isEligible: true,
        percentage: rawFirstOrder.percentage,
      }
    : undefined;

  /*
   * Use the backend-provided Gross Total (Net + Tax) for the line total display.
   * This ensures the displayed total includes taxes and matches the backend's
   * internal rounding logic exactly.
   */
  const calculatedLineTotalRaw = String(rawGrossInt);

  return {
    key: item.key,
    id: item.id,
    parentId,
    type: item.type,
    name: finalName,
    permalink: item.permalink,
    sku: item.sku,
    images: item.images,
    variation: finalVariation,
    shortDescription: item.short_description,
    description: item.description,
    quantity: item.quantity,
    quantityLimits,
    price: formatPrice(item.prices.price, currencyInfo),
    regularPrice: formatPrice(item.prices.regular_price, currencyInfo),
    salePrice: formatPrice(item.prices.sale_price, currencyInfo),
    catalogPrice,
    rawPrice: item.prices.price,
    rawRegularPrice: item.prices.regular_price,
    rawSalePrice: item.prices.sale_price,
    totals: itemTotals,
    calculatedLineTotal: calculatedLineTotalRaw,
    formattedCalculatedLineTotal: formatPrice(
      calculatedLineTotalRaw,
      currencyInfo,
    ),
    promotion,
    firstOrderDiscount, // Mapped First Order Discount
  };
};
